// Динамические счётчики для пунктов sidebar/drawer (рядом с label).
// Возвращает map `to → count`; sidebar override'ит `item.badge` отсюда.
//
// ┌─ Что здесь изменилось и почему ────────────────────────────────────────────┐
// │ Раньше это был СКРЫТЫЙ РЕЕСТР: 13 записей, захардкоженных по URL-строке,   │
// │ каждая со своим SQL прямо в файле. Модуль, добавленный в навигацию, бейджа  │
// │ не получал, пока кто-нибудь не вспомнит дописать сюда строку — а забыть    │
// │ это молча можно было навсегда.                                             │
// │                                                                            │
// │ Теперь SQL счётчика объявляет САМ модуль (`nav[].badge` в его манифесте), а │
// │ этот файл только СОБИРАЕТ запросы и раздаёт числа. Все PowerSync-счётчики  │
// │ склеены в ОДИН запрос со скалярными подзапросами: один watcher вместо      │
// │ десяти, и число хуков не зависит от числа модулей (иначе реестр из данных  │
// │ упирался бы в правила хуков React).                                        │
// └────────────────────────────────────────────────────────────────────────────┘
//
// Счётчик показывается на каждом пункте со списковыми сущностями, количество
// которых меняется (всё кроме Главной, Чата, Календаря и Прогресса). Источник —
// PowerSync (offline-first, локальный COUNT — реактивно и без сети) для
// синкаемых таблиц; visual-анализы server-only → tRPC.
//
// Приоритетный счётчик — /courses: число АКТИВНЫХ курсов (§9 Course Workspace
// V2, янтарный бейдж). /library — материалы вне курсов, нейтральный счётчик.

import { useMemo } from "react";
import { useVisuals } from "@/components/visual-explainer/_api";
import { useCurrentUser } from "@/hooks/use-session";
import { moduleNavBadges } from "@/lib/module-registry";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

// ─── Сборка запроса из реестра (один раз на загрузку модуля) ────────────────

const BADGES = moduleNavBadges();

/** Счётчики, выражаемые SQL по синкаемым таблицам. */
const POWERSYNC_BADGES = BADGES.flatMap((badge) =>
	badge.source.kind === "powersync" ? [{ to: badge.to, sql: badge.source.sql }] : [],
);

/**
 * Счётчики, которые обязан посчитать код (серверные данные без синка).
 * Экспортируется ради гейта: реестр не может знать, что хук написан.
 */
export const CLIENT_BADGE_KEYS: readonly string[] = BADGES.flatMap((badge) =>
	badge.source.kind === "client" ? [badge.to] : [],
);

/** Каждый `?` в SQL счётчика — это `user_id` (контракт `NavBadgeSource`). */
function placeholderCount(sql: string): number {
	return (sql.match(/\?/g) ?? []).length;
}

const BADGE_SQL =
	POWERSYNC_BADGES.length > 0
		? `SELECT ${POWERSYNC_BADGES.map((badge, i) => `(${badge.sql}) AS b${i}`).join(", ")}`
		: "SELECT 1 AS b0";

const BADGE_PARAM_COUNT = POWERSYNC_BADGES.reduce(
	(total, badge) => total + placeholderCount(badge.sql),
	0,
);

// ─── Хук ────────────────────────────────────────────────────────────────────

export function useNavBadges(): Record<string, number> {
	// Счётчики принадлежат КОНКРЕТНОМУ пользователю. Пустой id (гость, момент
	// между выходом и входом) обязан давать пустую карту, а не последние
	// известные числа: иначе новый аккаунт в той же вкладке видит чужие остатки.
	const userId = useCurrentUser()?.id ?? "";
	const params = useMemo(() => Array.from({ length: BADGE_PARAM_COUNT }, () => userId), [userId]);
	const counts = usePowerSyncQuery<Record<string, number>>(BADGE_SQL, params);

	// Единственный счётчик вне PowerSync: `visual_analyses` — серверная таблица.
	const visualsQuery = useVisuals();
	const visualsCount = visualsQuery.data?.items.length ?? 0;

	const row = counts.data?.[0];

	return useMemo(() => {
		if (userId === "") return {};
		const out: Record<string, number> = {};
		POWERSYNC_BADGES.forEach((badge, i) => {
			out[badge.to] = row?.[`b${i}`] ?? 0;
		});
		// Карта «путь → значение» для client-счётчиков. Ключи сверяются с
		// реестром гейтом `lib/module-registry.test.ts`: модуль, объявивший
		// `kind: "client"` без строки здесь, останется без бейджа молча.
		const clientValues: Record<string, number> = { "/tools/visual-explainer": visualsCount };
		for (const key of CLIENT_BADGE_KEYS) {
			const value = clientValues[key];
			if (value !== undefined) out[key] = value;
		}
		return out;
	}, [row, userId, visualsCount]);
}
